<div class="min-h-screen">
  <div class="px-4 py-5 md:px-6 md:py-6 max-w-full">

    <!-- Header -->
    <h1 class="text-xl md:text-2xl font-bold text-gray-900 tracking-tight mb-4 md:mb-6">
      {{ 'allApplications.title' | translate }}
    </h1>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="flex items-center justify-center py-24">
      <div class="flex flex-col items-center gap-3">
        <svg class="animate-spin w-8 h-8 text-rose-500" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
        </svg>
        <p class="text-sm text-gray-400">{{ 'allApplications.loading' | translate }}</p>
      </div>
    </div>

    <!-- Error State -->
    <div *ngIf="!isLoading && errorMessage" class="flex flex-col items-center justify-center py-20 text-center gap-3">
      <p class="text-sm text-gray-500">{{ errorMessage }}</p>
      <button (click)="loadApplications()"
        class="mt-1 px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition">
        {{ 'allApplications.retry' | translate }}
      </button>
    </div>

    <!-- Main Content -->
    <ng-container *ngIf="!isLoading && !errorMessage">

      <!-- Search -->
      <div class="relative mb-4 md:mb-5">
        <span class="absolute left-3 md:left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </span>
        <input type="text" [(ngModel)]="searchQuery" (ngModelChange)="onSearchChange()"
          [placeholder]="'allApplications.searchPlaceholder' | translate"
          class="w-full pl-9 md:pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 bg-white outline-none transition focus:border-rose-400 focus:ring-2 focus:ring-rose-100 placeholder-gray-400" />
      </div>

      <!-- Tabs + Filters -->
      <div class="flex items-center justify-between flex-wrap gap-3 border-b border-gray-200 mb-4">

        <!-- Tabs -->
        <div class="flex items-end overflow-x-auto scrollbar-hide">
          <button
            class="px-3 md:px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap"
            [ngClass]="activeTab === 'all'
              ? 'border-brand text-brand font-semibold'
              : 'border-transparent text-gray-500 hover:text-gray-700'" (click)="setActiveTab('all')">
            {{ 'allApplications.all' | translate }} <span class="hidden md:inline">{{ 'allApplications.applications' | translate }}</span>
            <span class="text-xs rounded-full px-2 py-0.5 font-medium"
              [ngClass]="activeTab === 'all' ? 'tab-badge-active' : 'bg-gray-100 text-gray-500'">
              {{ totalCount }}
            </span>
          </button>
          <button
            class="px-3 md:px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap"
            [ngClass]="activeTab === 'other'
              ? 'border-brand text-brand font-semibold'
              : 'border-transparent text-gray-500 hover:text-gray-700'" (click)="setActiveTab('other')">
            {{ 'allApplications.reviewed' | translate }}
            <span class="text-xs rounded-full px-2 py-0.5 font-medium"
              [ngClass]="activeTab === 'other' ? 'tab-badge-active' : 'bg-gray-100 text-gray-500'">
              {{ otherCount }}
            </span>
          </button>
          <button
            class="px-3 md:px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap"
            [ngClass]="activeTab === 'submitted'
              ? 'border-brand text-brand font-semibold'
              : 'border-transparent text-gray-500 hover:text-gray-700'" (click)="setActiveTab('submitted')">
            {{ 'allApplications.unreviewed' | translate }}
            <span class="text-xs rounded-full px-2 py-0.5 font-medium"
              [ngClass]="activeTab === 'submitted' ? 'tab-badge-active' : 'bg-gray-100 text-gray-500'">
              {{ submittedCount }}
            </span>
          </button>
        </div>

        <!-- Filter Pills -->
        <div class="flex gap-2 flex-wrap pb-3 overflow-x-auto scrollbar-hide">

          <!-- Position Title -->
          <div class="relative flex-shrink-0">
            <button (click)="toggleDropdown('position', $event)"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border transition-all whitespace-nowrap"
              [ngClass]="filters.positionTitle
                ? 'border-rose-400 text-rose-500 bg-rose-50'
                : 'border-gray-200 text-gray-600 bg-white hover:border-gray-400'">
              <span>{{ filters.positionTitle || ('allApplications.position' | translate) }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
              <span *ngIf="filters.positionTitle" class="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
            </button>
          </div>

          <!-- Status -->
          <div class="relative flex-shrink-0">
            <button (click)="toggleDropdown('status', $event)"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border transition-all whitespace-nowrap"
              [ngClass]="filters.status
                ? 'border-rose-400 text-rose-500 bg-rose-50'
                : 'border-gray-200 text-gray-600 bg-white hover:border-gray-400'">
              <span>{{ filters.status || ('allApplications.status' | translate) }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
              <span *ngIf="filters.status" class="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
            </button>
          </div>

        </div>
      </div>

      <!-- Dropdowns rendered here OUTSIDE table using fixed position -->
      <div *ngIf="openDropdown === 'position'"
        class="fixed min-w-[160px] bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] p-1.5 flex flex-col gap-0.5"
        [style.top.px]="dropdownTop"
        [style.left.px]="dropdownLeft">
        <div *ngFor="let title of positionTitles" (click)="setFilter('positionTitle', title)"
          class="px-3.5 py-2 text-sm cursor-pointer rounded-lg transition-colors"
          [ngClass]="filters.positionTitle === title
            ? 'bg-rose-50 text-rose-600 font-semibold'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'">
          {{ title }}
        </div>
        <div *ngIf="filters.positionTitle" class="h-px bg-gray-100 my-1"></div>
        <div *ngIf="filters.positionTitle" (click)="clearFilter('positionTitle')"
          class="px-3.5 py-2 text-xs text-red-500 cursor-pointer rounded-lg hover:bg-red-50 transition-colors">
          {{ 'allApplications.clear' | translate }}
        </div>
      </div>

      <div *ngIf="openDropdown === 'status'"
        class="fixed min-w-[160px] bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] p-1.5 flex flex-col gap-0.5"
        [style.top.px]="dropdownTop"
        [style.left.px]="dropdownLeft">
        <div *ngFor="let s of statuses" (click)="setFilter('status', s)"
          class="px-3.5 py-2 text-sm cursor-pointer rounded-lg transition-colors"
          [ngClass]="filters.status === s
            ? 'bg-rose-50 text-rose-600 font-semibold'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'">
          {{ s }}
        </div>
        <div *ngIf="filters.status" class="h-px bg-gray-100 my-1"></div>
        <div *ngIf="filters.status" (click)="clearFilter('status')"
          class="px-3.5 py-2 text-xs text-red-500 cursor-pointer rounded-lg hover:bg-red-50 transition-colors">
          {{ 'allApplications.clear' | translate }}
        </div>
      </div>

      <!-- DESKTOP: Table -->
      <div class="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50 border-b border-gray-200">
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {{ 'allApplications.applicantName' | translate }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {{ 'allApplications.email' | translate }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {{ 'allApplications.position' | translate }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {{ 'allApplications.status' | translate }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {{ 'allApplications.appliedAt' | translate }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let app of paginatedApplications" (click)="goToDetails(app.id)"
                class="border-b border-gray-100 last:border-0 hover:bg-gray-200 transition-colors cursor-pointer">
                <td class="px-4 py-3.5">
                  <div class="flex items-center gap-2.5">
                    <div
                      class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      [ngClass]="getAvatarColor(app.applicantName)">
                      {{ app.applicantName.charAt(0) }}
                    </div>
                    <span class="text-sm text-gray-700 font-medium">{{ app.applicantName }}</span>
                  </div>
                </td>
                <td class="px-4 py-3.5 text-gray-500 text-xs">{{ app.applicantEmail }}</td>
                <td class="px-4 py-3.5">
                  <span class="inline-block px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                    {{ app.positionTitle }}
                  </span>
                </td>
                <td class="px-4 py-3.5">
                  <span class="inline-block px-2.5 py-1 rounded-full text-xs font-medium"
                    [ngClass]="getStatusBadgeClass(app.status)">
                    {{ app.status }}
                  </span>
                </td>
                <td class="px-4 py-3.5 text-gray-500 text-xs">{{ formatDate(app.appliedAt) }}</td>
              </tr>
              <tr *ngIf="filteredApplications.length === 0">
                <td colspan="7" class="text-center py-16 text-gray-400">
                  <p class="text-sm">{{ 'allApplications.noApplications' | translate }}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Desktop Pagination -->
        <div *ngIf="totalPages > 1"
          class="px-4 py-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <button (click)="goToPage(currentPage - 1)" [disabled]="currentPage === 1"
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            {{ 'allApplications.prev' | translate }}
          </button>
          <div class="flex items-center gap-1">
            <ng-container *ngFor="let page of pageNumbers">
              <span *ngIf="page === -1" class="px-1 text-gray-400 text-sm select-none">…</span>
              <button *ngIf="page !== -1" (click)="goToPage(page)"
                class="w-8 h-8 rounded-lg text-sm font-medium transition"
                [ngClass]="page === currentPage ? 'page-active' : 'text-gray-600 hover:bg-gray-100'">
                {{ page }}
              </button>
            </ng-container>
          </div>
          <button (click)="goToPage(currentPage + 1)" [disabled]="currentPage === totalPages"
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
            {{ 'allApplications.next' | translate }}
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        <!-- Desktop Footer -->
        <div class="px-4 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
          {{ 'allApplications.showing' | translate }} <strong class="text-gray-700">{{ paginatedApplications.length }}</strong>
          {{ 'allApplications.of' | translate }} <strong class="text-gray-700">{{ filteredApplications.length }}</strong> {{ 'allApplications.applications' | translate }}
        </div>
      </div>

      <!-- MOBILE: Cards -->
      <div class="flex flex-col gap-3 md:hidden">
        <div *ngFor="let app of paginatedApplications"
          class="app-card bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div class="flex items-start justify-between px-4 pt-4 pb-3 border-b border-gray-100">
            <div class="flex items-center gap-2.5">
              <div
                class="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                [ngClass]="getAvatarColor(app.applicantName)">
                {{ app.applicantName.charAt(0) }}
              </div>
              <div>
                <p class="font-semibold text-gray-900 text-sm leading-tight">{{ app.applicantName }}</p>
                <p class="text-xs text-gray-400 mt-0.5">{{ formatDate(app.appliedAt) }}</p>
              </div>
            </div>
            <span class="inline-block px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 mt-0.5"
              [ngClass]="getStatusBadgeClass(app.status)">
              {{ app.status }}
            </span>
          </div>

          <div class="grid grid-cols-2 gap-x-3 px-4 py-3">
            <div class="info-field">
              <span class="field-label">{{ 'allApplications.email' | translate }}</span>
              <span class="field-value text-blue-600 truncate block">{{ app.applicantEmail }}</span>
            </div>
            <div class="info-field">
              <span class="field-label">{{ 'allApplications.position' | translate }}</span>
              <span class="field-value">{{ app.positionTitle }}</span>
            </div>
          </div>

          <div *ngIf="app.answers && app.answers.length > 0" class="px-4 pb-3 border-t border-gray-100 pt-2">
            <p class="text-xs text-gray-400 font-medium mb-1">
              {{ app.answers.length }} {{ 'allApplications.answer' | translate }}{{ app.answers.length > 1 ? 's' : '' }} {{ 'allApplications.submitted' | translate }}
            </p>
          </div>
        </div>

        <div *ngIf="filteredApplications.length === 0"
          class="bg-white rounded-2xl border border-gray-200 py-16 flex flex-col items-center text-center">
          <div class="text-3xl mb-2">🔍</div>
          <p class="text-sm text-gray-400">{{ 'allApplications.noApplications' | translate }}</p>
        </div>
      </div>

      <!-- MOBILE: Pagination + Footer -->
      <div class="md:hidden">
        <div *ngIf="totalPages > 1" class="flex items-center justify-between mt-4 gap-2">
          <button (click)="goToPage(currentPage - 1)" [disabled]="currentPage === 1"
            class="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-xl border border-gray-200 text-gray-600 bg-white disabled:opacity-40 disabled:cursor-not-allowed transition">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            {{ 'allApplications.prev' | translate }}
          </button>
          <div class="flex items-center gap-1">
            <ng-container *ngFor="let page of pageNumbers">
              <span *ngIf="page === -1" class="px-1 text-gray-400 text-sm select-none">…</span>
              <button *ngIf="page !== -1" (click)="goToPage(page)"
                class="w-8 h-8 rounded-lg text-sm font-medium transition"
                [ngClass]="page === currentPage ? 'page-active' : 'text-gray-600 hover:bg-gray-100'">
                {{ page }}
              </button>
            </ng-container>
          </div>
          <button (click)="goToPage(currentPage + 1)" [disabled]="currentPage === totalPages"
            class="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-xl border border-gray-200 text-gray-600 bg-white disabled:opacity-40 disabled:cursor-not-allowed transition">
            {{ 'allApplications.next' | translate }}
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
        <div class="mt-3 text-xs text-gray-400 text-center pb-4">
          {{ 'allApplications.showing' | translate }} <strong class="text-gray-600">{{ paginatedApplications.length }}</strong>
          {{ 'allApplications.of' | translate }} <strong class="text-gray-600">{{ filteredApplications.length }}</strong> {{ 'allApplications.applications' | translate }}
        </div>
      </div>

    </ng-container>
  </div>
</div>

<!-- Backdrop -->
<div *ngIf="openDropdown" class="fixed inset-0 z-40" (click)="openDropdown = null"></div>